import { FormEvent, KeyboardEvent, startTransition, useDeferredValue, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Moon, PanelLeftClose, Search, Sparkles, Sun } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { searchFiles } from "@/api/search";
import { getStorageUsage } from "@/api/storage";
import type { SearchResult } from "@/api/types";
import { AgentToggleButton } from "@/components/agent/AgentToggleButton";
import { FileIcon } from "@/components/common/FileIcon";
import { InstallAppButton } from "@/components/common/InstallAppButton";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { formatBytes, formatRelativeTime, isOfficeFile } from "@/lib/utils";
import { useAgentStore } from "@/store/agentStore";
import { useUiStore } from "@/store/uiStore";

interface HeaderProps {
  variant: "user" | "admin";
}

export function Header({ variant }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const trimmedQuery = query.trim();
  const deferredQuery = useDeferredValue(trimmedQuery);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);
  const collapsed = useUiStore((state) => state.sidebarCollapsed);
  const setCommandPaletteOpen = useUiStore((state) => state.setCommandPaletteOpen);
  const agentPanelOpen = useAgentStore((state) => state.panelOpen);
  const toggleAgentPanel = useAgentStore((state) => state.togglePanel);
  const { theme, setTheme } = useTheme();
  const { user, tenant, logoutMutation, refreshToken } = useAuth();

  const usageQuery = useQuery({
    queryKey: ["header-usage", tenant?.id],
    queryFn: () => getStorageUsage(),
    enabled: variant === "user" && Boolean(user) && Boolean(tenant),
    retry: false,
    staleTime: 300_000,
  });

  const usagePercent = usageQuery.data?.tenant_quota
    ? Math.min(100, Math.round((usageQuery.data.tenant_used / usageQuery.data.tenant_quota) * 100))
    : 0;

  const quickSearchQuery = useQuery({
    queryKey: ["search", "header", deferredQuery],
    queryFn: () => searchFiles(deferredQuery, 8, 0),
    enabled: variant === "user" && searchFocused && deferredQuery.length > 0,
    staleTime: 30_000,
    gcTime: 300_000,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
  });

  const showQuickResults = variant === "user" && searchFocused && trimmedQuery.length > 0;
  const quickResults = quickSearchQuery.data?.items ?? [];

  const searchHint = useMemo(() => {
    if (variant !== "user" || !usageQuery.data) {
      return null;
    }

    return {
      used: formatBytes(usageQuery.data.tenant_used),
      total: formatBytes(usageQuery.data.tenant_quota),
      percent: usagePercent,
    };
  }, [usagePercent, usageQuery.data, variant]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchValue = variant === "admin" ? params.get("query") : params.get("q");
    const onSearchRoute =
      (variant === "user" && location.pathname === "/app/search") ||
      (variant === "admin" && location.pathname === "/admin/audit");

    if (onSearchRoute) {
      setQuery(searchValue ?? "");
    }
  }, [location.pathname, location.search, variant]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    if (!trimmedQuery) {
      return;
    }

    setSearchFocused(false);
    startTransition(() => {
      navigate(
        variant === "admin"
          ? `/admin/audit?query=${encodeURIComponent(trimmedQuery)}`
          : `/app/search?q=${encodeURIComponent(trimmedQuery)}`,
      );
    });
  };

  const openQuickResult = (item: SearchResult) => {
    setSearchFocused(false);
    startTransition(() => {
      if (isOfficeFile(item.mime_type, item.name)) {
        navigate(`/app/office/${item.id}`);
        return;
      }

      navigate(`/app/files/preview/${item.id}`);
    });
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setSearchFocused(false);
      return;
    }

    if (event.key === "ArrowDown" && showQuickResults) {
      event.preventDefault();
      const firstResult = document.querySelector<HTMLButtonElement>("[data-search-result='true']");
      firstResult?.focus();
    }
  };

  return (
    <header className="sticky top-0 z-30 isolate border-b border-border-subtle/60 bg-background-primary/92 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1720px] items-center gap-2 px-4 py-1 sm:px-6 lg:px-8 2xl:px-10">
        <Button
          aria-label="Collapse sidebar"
          className="hidden h-9 w-9 rounded-lg border-border/70 bg-background-elevated/80 shadow-sm lg:inline-flex"
          size="icon"
          variant="secondary"
          onClick={toggleSidebar}
        >
          <PanelLeftClose className={`h-3.5 w-3.5 transition-transform duration-200 ${collapsed ? "rotate-180" : ""}`} />
        </Button>

        <form className="relative z-20 min-w-0 max-w-[420px] flex-1" onSubmit={submitSearch}>
          <div
            className={`relative flex w-full items-center rounded-xl border border-border/60 bg-background-elevated/85 px-1.5 shadow-xs transition-all duration-200 ${
              searchFocused ? "border-accent/40 ring-3 ring-accent/8" : ""
            }`}
          >
            <Search
              className={`pointer-events-none ml-2 h-3.5 w-3.5 -translate-y-0.5 transition-colors duration-150 ${
                searchFocused ? "text-accent" : "text-text-tertiary"
              }`}
            />
            <Input
              aria-label="Search"
              className="h-9 rounded-lg border-0 bg-transparent pl-2 pr-16 text-xs shadow-none placeholder:text-text-tertiary focus-visible:border-0 focus-visible:ring-0"
              placeholder={
                variant === "admin"
                  ? "Search audit events, actors..."
                  : "Search files, folders, photos..."
              }
              value={query}
              onBlur={() => setSearchFocused(false)}
              onChange={(event) => setQuery(event.target.value)}
              onFocus={() => setSearchFocused(true)}
              onKeyDown={handleSearchKeyDown}
            />
            <button
              type="button"
              className="absolute right-1 hidden items-center gap-0.5 rounded-lg border border-border/50 bg-background-secondary/60 px-1.5 py-0.5 text-[9px] font-medium text-text-tertiary transition-colors hover:text-text-secondary sm:flex"
              onClick={() => setCommandPaletteOpen(true)}
            >
              <Sparkles className="h-2.5 w-2.5" />
              <span className="hidden sm:inline">Cmd+K</span>
            </button>
          </div>

          {showQuickResults ? (
            <>
              <div className="fixed inset-x-0 bottom-0 top-[64px] z-30 bg-background-primary/60 backdrop-blur-sm" />
              <div className="absolute left-0 right-0 top-[calc(100%+0.75rem)] z-40 overflow-hidden rounded-[24px] border border-border/80 bg-background-primary shadow-2xl shadow-black/35 ring-1 ring-black/10">
                <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">Quick results</p>
                  <p className="text-xs text-text-tertiary">
                    {quickSearchQuery.isFetching ? "Searching your workspace..." : "Open a file right away or see every result."}
                  </p>
                </div>
                {quickSearchQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin text-accent" /> : null}
              </div>

              <div className="max-h-[420px] overflow-y-auto p-2">
                {!quickSearchQuery.isFetching && quickResults.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-border/70 bg-background-secondary/50 px-4 py-6 text-center">
                    <p className="text-sm font-medium text-text-primary">No matching files yet</p>
                    <p className="mt-1 text-xs text-text-tertiary">
                      Try another keyword, file extension, or open the full search page.
                    </p>
                  </div>
                ) : null}

                {quickResults.map((item) => (
                  <button
                    key={item.id}
                    data-search-result="true"
                    className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition-colors duration-150 hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => openQuickResult(item)}
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-border/70 bg-background-secondary/70">
                      <FileIcon className="h-5 w-5 text-text-secondary" fileName={item.name} mimeType={item.mime_type} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-text-primary">{item.name}</p>
                      <p className="truncate text-xs text-text-tertiary">
                        {formatRelativeTime(item.created_at)}
                        {item.size > 0 ? ` - ${formatBytes(item.size)}` : ""}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

                <div className="border-t border-border/60 p-2">
                  <button
                    className="flex w-full items-center justify-between rounded-2xl px-3 py-3 text-left text-sm font-semibold text-accent transition-colors duration-150 hover:bg-accent-subtle focus-visible:bg-accent-subtle focus-visible:outline-none"
                    type="submit"
                    onMouseDown={(event) => event.preventDefault()}
                >
                  <span>See all results for "{trimmedQuery}"</span>
                  <Search className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </form>

        {searchHint ? (
          <div className="shrink-0 rounded-lg border border-border/50 bg-background-secondary/40 px-2 py-1">
            <div className="flex items-center gap-2">
              <CircularUsageGauge percent={searchHint.percent} />
              <div className="min-w-0 hidden md:block">
                <p className="text-[9px] font-bold uppercase tracking-wider text-text-tertiary">Storage</p>
                <p className="text-xs font-semibold leading-tight text-text-primary">
                  {searchHint.used}<span className="text-[10px] font-normal text-text-tertiary"> / {searchHint.total}</span>
                </p>
              </div>
            </div>
          </div>
        ) : null}

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {variant === "user" ? (
            <InstallAppButton className="hidden rounded-2xl lg:inline-flex" compactLabel="Install" label="Install app" />
          ) : null}

          {variant === "user" ? (
            <AgentToggleButton active={agentPanelOpen} onClick={toggleAgentPanel} />
          ) : null}

          <Button
            aria-label="Toggle theme"
            className="h-9 w-9 rounded-lg border-border/70 bg-background-elevated/80 text-text-tertiary shadow-sm hover:text-text-primary"
            size="icon"
            variant="secondary"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          >
            {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-lg border border-border/70 bg-background-elevated/90 py-0.5 pl-0.5 pr-2 shadow-sm transition-all duration-150 hover:border-border-strong hover:bg-background-elevated hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30">
                <UserAvatar
                  className="h-9 w-9 border border-border-subtle bg-background-secondary"
                  name={user?.display_name ?? user?.email ?? "User"}
                />
                <div className="hidden text-left sm:block">
                  <p className="text-xs font-semibold leading-tight text-text-primary">
                    {user?.display_name ?? user?.email ?? "Guest"}
                  </p>
                  <p className="text-[10px] leading-tight text-text-tertiary">
                    {tenant?.name ?? "seaDrive"}
                  </p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                Account
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate(variant === "admin" ? "/admin/settings" : "/app/settings")}
              >
                Profile &amp; Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate(variant === "admin" ? "/admin" : "/app/files")}
              >
                Home
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-danger focus:text-danger"
                onClick={() => logoutMutation.mutate({ refresh_token: refreshToken ?? "" })}
              >
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

function CircularUsageGauge({ percent }: { percent: number }) {
  const safePercent = Math.max(0, Math.min(100, percent));
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (safePercent / 100) * circumference;

  return (
    <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-background-secondary/70">
      <svg className="h-10 w-10 -rotate-90" viewBox="0 0 48 48">
        <circle cx="24" cy="24" r={radius} className="stroke-border/60" fill="none" strokeWidth="3" />
        <circle
          cx="24"
          cy="24"
          r={radius}
          className="stroke-accent transition-all duration-500 ease-out"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          strokeWidth="3"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[9px] font-bold text-accent">{safePercent}%</span>
      </div>
    </div>
  );
}
